import React, { useState, useEffect, useRef } from "react";
import { captureSubmissionError } from "../../utils/sentryUtils";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";
import { buildSignupSubmissionBody } from "./signupSubmissionBody";

const SignupSubmission = ({ signupFlowState: flowState, onFlowComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Prevents duplicate signup submissions caused by React 18 StrictMode
  // double-mounting effects in development. React docs recommend cleanup over
  // ref guards, but cleanup (abort) is wrong here because the PUT is not
  // idempotent and must run to completion so onFlowComplete() fires. The ref
  // persists across the StrictMode unmount-remount cycle, so the second mount
  // sees hasInitiated=true and skips the fetch. No abort cleanup is provided;
  // if the component unmounts mid-flight (e.g. browser back), the orphaned
  // .then() handlers will attempt state updates that React 18 silently ignores.
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    fetch("/api/signup", {
      method: "PUT",
      body: JSON.stringify(buildSignupSubmissionBody(flowState)),
    }).then(
      async (resp) => {
        if (resp.ok) {
          setLoading(false);
          onFlowComplete();
        } else {
          const body = await resp.text().catch(() => "");
          console.error("Signup submission failed:", resp.status, body);
          captureSubmissionError("Signup", resp.status, body);
          setError("Unable to process your signup");
          setLoading(false);
        }
      },
      (err) => {
        console.error("Signup submission failed:", err);
        setError("Unable to process your signup");
        setLoading(false);
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fires once on mount; ref guard prevents StrictMode re-fire; props are stable for component lifetime

  if (loading) {
    return <SubmittingCard />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

export default SignupSubmission;
